import * as preferences from '@/api/adapters/supabase/preferences'
import * as openWorldResponses from '@/api/adapters/supabase/open-world-responses'
import * as openWorldPreparations from '@/api/adapters/supabase/open-world-preparations'
import * as openWorldCanon from '@/api/adapters/supabase/open-world-canon'
// Supabase API Adapter - barrel file
// Assembles domain modules into the SupabaseAdapter export

import * as auth from './auth'
import * as authSession from '@/api/adapters/supabase/auth-session'
import * as promptCaptures from '@/api/adapters/supabase/prompt-captures'
import * as characters from './characters'
import * as lorebooks from './lorebooks'
import * as worlds from './worlds'
import * as worldWorkspaces from './world-workspaces'
import * as creatorWorkspaces from './creator-workspaces'
import * as creatorSessions from './creator-sessions'
import * as conversations from './conversations'
import * as messages from './messages'
import * as roleplayMessages from '@/api/adapters/supabase/roleplay-messages'
import * as directorNotes from './director-notes'
import * as personas from './personas'
import * as bots from './bots'
import * as openWorlds from '@/api/adapters/supabase/open-worlds'
import * as openWorldAttempts from '@/api/adapters/supabase/open-world-attempts'
import * as openWorldJournal from '@/api/adapters/supabase/open-world-journal'
import * as openWorldNotes from '@/api/adapters/supabase/open-world-notes'

export const SupabaseAdapter = {
  ...preferences,
  ...promptCaptures,
  // Authentication
  ...authSession,
  getMyProfile: auth.getMyProfile,
  getPublicProfile: auth.getPublicProfile,
  updateMyProfile: auth.updateMyProfile,
  requestPasswordReset: auth.requestPasswordReset,
  updatePassword: auth.updatePassword,
  createAccount: auth.createAccount,
  requestAccess: auth.requestAccess,
  adminAccess: auth.adminAccess,

  // Characters
  getCharacters: characters.getCharacters,
  getMyCharacters: characters.getMyCharacters,
  browseCharacters: characters.browseCharacters,
  getCharacter: characters.getCharacter,
  setCharacterFavorite: characters.setCharacterFavorite,
  listFavoriteCharacters: characters.listFavoriteCharacters,
  createCharacter: characters.createCharacter,
  updateCharacter: characters.updateCharacter,
  deleteCharacter: characters.deleteCharacter,
  getTags: characters.getTags,
  getPopularTags: characters.getPopularTags,

  // Lorebooks
  getLorebooks: lorebooks.getLorebooks,
  getLorebook: lorebooks.getLorebook,
  createLorebook: lorebooks.createLorebook,
  updateLorebook: lorebooks.updateLorebook,
  deleteLorebook: lorebooks.deleteLorebook,

  // Worlds
  getWorlds: worlds.getWorlds,
  getWorld: worlds.getWorld,
  getPublicWorld: worlds.getPublicWorld,
  createWorld: worlds.createWorld,
  updateWorld: worlds.updateWorld,
  deleteWorld: worlds.deleteWorld,

  // World workspaces (world-only draft/publish flows)
  getWorldWorkspace: worldWorkspaces.getWorldWorkspace,
  ensureWorldWorkspace: worldWorkspaces.ensureWorldWorkspace,
  createWorldDraftProposal: worldWorkspaces.createWorldDraftProposal,
  approveWorldDraftProposal: worldWorkspaces.approveWorldDraftProposal,
  rejectWorldDraftProposal: worldWorkspaces.rejectWorldDraftProposal,
  publishWorldWorkspace: worldWorkspaces.publishWorldWorkspace,

  // Creator workspaces (generic across entity types)
  ensureCreatorWorkspace: creatorWorkspaces.ensureCreatorWorkspace,
  getCreatorWorkspace: creatorWorkspaces.getCreatorWorkspace,
  getCreatorWorkspaceLoad: creatorWorkspaces.getCreatorWorkspaceLoad,
  getCreatorWorkspaceSummaries: creatorWorkspaces.getCreatorWorkspaceSummaries,
  resolveCreatorWorkspaceRoute: creatorWorkspaces.resolveCreatorWorkspaceRoute,
  createCreatorWorkspaceConversation: creatorWorkspaces.createCreatorWorkspaceConversation,
  updateCreatorWorkspaceDraft: creatorWorkspaces.updateCreatorWorkspaceDraft,
  commitCreatorWorkspace: creatorWorkspaces.commitCreatorWorkspace,
  finalizeCreatorWorkspace: creatorWorkspaces.finalizeCreatorWorkspace,
  deleteCreatorWorkspace: creatorWorkspaces.deleteCreatorWorkspace,
  listWorkspaceConversations: creatorWorkspaces.listWorkspaceConversations,
  setWorkspaceConversationBot: creatorWorkspaces.setWorkspaceConversationBot,
  setWorkspaceConversationPromptSettings: creatorWorkspaces.setWorkspaceConversationPromptSettings,
  renameWorkspaceConversation: creatorWorkspaces.renameWorkspaceConversation,
  deleteWorkspaceConversation: creatorWorkspaces.deleteWorkspaceConversation,
  createWorkspaceConversationMessage: creatorWorkspaces.createWorkspaceConversationMessage,
  createWorkspaceConversationEvent: creatorWorkspaces.createWorkspaceConversationEvent,
  rewindWorkspaceConversation: creatorWorkspaces.rewindWorkspaceConversation,

  // Creator sessions (legacy vocabulary emulation over workspaces)
  createCreatorSession: creatorSessions.createCreatorSession,
  getCreatorConversationState: creatorSessions.getCreatorConversationState,
  updateCreatorConversation: creatorSessions.updateCreatorConversation,
  updateCreatorSessionWithEvents: creatorSessions.updateCreatorSessionWithEvents,
  getCreatorSessionEvents: creatorSessions.getCreatorSessionEvents,
  createCreatorSessionEvent: creatorSessions.createCreatorSessionEvent,
  getCreatorSessionMessages: creatorSessions.getCreatorSessionMessages,
  createCreatorMessage: creatorSessions.createCreatorMessage,
  updateCreatorMessage: creatorSessions.updateCreatorMessage,
  deleteCreatorMessages: creatorSessions.deleteCreatorMessages,
  deleteCreatorAssistantTurn: creatorSessions.deleteCreatorAssistantTurn,
  truncateCreatorSessionFromTurn: creatorSessions.truncateCreatorSessionFromTurn,
  clearCreatorSessionTranscript: creatorSessions.clearCreatorSessionTranscript,

  // Conversations
  getConversations: conversations.getConversations,
  getConversation: conversations.getConversation,
  createConversation: conversations.createConversation,
  forkConversationAtMessage: conversations.forkConversationAtMessage,
  updateConversation: conversations.updateConversation,
  deleteConversation: conversations.deleteConversation,
  getConversationMessages: conversations.getConversationMessages,
  getConversationContext: conversations.getConversationContext,
  ensureConversationIntroSlot: conversations.ensureConversationIntroSlot,

  // Messages
  ...roleplayMessages,
  updateMessage: messages.updateMessage,

  // Director notes
  updateMessageDirectorNote: directorNotes.updateMessageDirectorNote,
  deleteMessageDirectorNote: directorNotes.deleteMessageDirectorNote,

  // Message Alternatives
  getMessageAlternatives: messages.getMessageAlternatives,

  // Personas
  getPersonas: personas.getPersonas,
  getPersona: personas.getPersona,
  createPersona: personas.createPersona,
  updatePersona: personas.updatePersona,
  deletePersona: personas.deletePersona,
  setDefaultPersona: personas.setDefaultPersona,

  // Bots
  getBots: bots.getBots,
  createBot: bots.createBot,
  updateBot: bots.updateBot,
  deleteBot: bots.deleteBot,
  setDefaultBot: bots.setDefaultBot,

  // Open Worlds
  syncOpenWorldAvatarLeases: openWorlds.syncOpenWorldAvatarLeases,
  saveOpenWorldDefinition: openWorlds.saveOpenWorldDefinition,
  getOpenWorldDefinition: openWorlds.getOpenWorldDefinition,
  listOpenWorldDefinitions: openWorlds.listOpenWorldDefinitions,
  createOpenWorldPlaythrough: openWorlds.createOpenWorldPlaythrough,
  forkOpenWorldPlaythrough: openWorlds.forkOpenWorldPlaythrough,
  attachOpenWorldPersona: openWorlds.attachOpenWorldPersona,
  deleteOpenWorldPlaythrough: openWorlds.deleteOpenWorldPlaythrough,
  getOpenWorldPlaythrough: openWorlds.getOpenWorldPlaythrough,
  listOpenWorldPlaythroughs: openWorlds.listOpenWorldPlaythroughs,
  listOpenWorldResponses: openWorldResponses.listOpenWorldResponses,
  getOpenWorldResponseEvidence: openWorldResponses.getOpenWorldResponseEvidence,
  beginOpenWorldResponse: openWorldResponses.beginOpenWorldResponse,
  renewOpenWorldResponse: openWorldResponses.renewOpenWorldResponse,
  finishOpenWorldResponse: openWorldResponses.finishOpenWorldResponse,
  selectOpenWorldResponse: openWorldResponses.selectOpenWorldResponse,
  recoverOpenWorldResponse: openWorldResponses.recoverOpenWorldResponse,
  saveOpenWorldPreparation: openWorldPreparations.saveOpenWorldPreparation,
  getOpenWorldPreparation: openWorldPreparations.getOpenWorldPreparation,
  getOpenWorldCanonInput: openWorldCanon.getOpenWorldCanonInput,
  beginOpenWorldAttempt: openWorldAttempts.beginOpenWorldAttempt,
  renewOpenWorldAttemptLease: openWorldAttempts.renewOpenWorldAttemptLease,
  checkpointOpenWorldAttempt: openWorldAttempts.checkpointOpenWorldAttempt,
  terminateOpenWorldAttempt: openWorldAttempts.terminateOpenWorldAttempt,
  recoverOpenWorldAttempt: openWorldAttempts.recoverOpenWorldAttempt,
  getOpenWorldAttempt: openWorldAttempts.getOpenWorldAttempt,
  listOpenWorldAttempts: openWorldAttempts.listOpenWorldAttempts,
  startOpenWorldJournal: openWorldJournal.startOpenWorldJournal,
  finishOpenWorldJournal: openWorldJournal.finishOpenWorldJournal,
  publishOpenWorldAttempt: openWorldJournal.publishOpenWorldAttempt,
  getOpenWorldJournal: openWorldJournal.getOpenWorldJournal,
  getOpenWorldActiveBranch: openWorldJournal.getOpenWorldActiveBranch,
  getOpenWorldStorySnapshot: openWorldJournal.getOpenWorldStorySnapshot,
  writeOpenWorldNote: openWorldNotes.writeOpenWorldNote,
  getOpenWorldNoteEvidence: openWorldNotes.getOpenWorldNoteEvidence,
  beginOpenWorldNote: openWorldNotes.beginOpenWorldNote,
  finishOpenWorldNote: openWorldNotes.finishOpenWorldNote,
  recoverOpenWorldNote: openWorldNotes.recoverOpenWorldNote,
  editOpenWorldNote: openWorldJournal.editOpenWorldNote,
  setOpenWorldNoteTaking: openWorldJournal.setOpenWorldNoteTaking,
  editOpenWorldMessage: openWorldJournal.editOpenWorldMessage,
  rewindOpenWorldStory: openWorldJournal.rewindOpenWorldStory,
  changeOpenWorldConversation: openWorldJournal.changeOpenWorldConversation,
  changeOpenWorldPlayerMessage: openWorldJournal.changeOpenWorldPlayerMessage,
}

export default SupabaseAdapter
